import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';
import { DiscountPolicyError, beginDiscountOperation, singleDiscountCode, isWelcomeCoupon, isWalletCoupon, consumeWalletCoupon, discountConflictMessage, readCurrentDiscountCodes } from '@calvielle/discount-policy';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorPolicy - The coupon/code policy notice.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorPolicy'];

  /** @type {AbortController | null} */
  #activeFetch = null;
  #releaseOperation = null;

  #createAbortController() {
    const release = beginDiscountOperation();
    if (!release) {
      this.#showPolicyError('Please wait for the current discount update to finish.');
      return null;
    }
    this.#releaseOperation = release;
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    this.setAttribute('aria-busy', 'true');
    this.#clearErrors();
    return abortController;
  }

  #finishOperation(controller) {
    if (this.#activeFetch !== controller) return;
    this.#activeFetch = null;
    this.#releaseOperation?.();
    this.#releaseOperation = null;
    this.removeAttribute('aria-busy');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
  }

  #clearErrors() {
    for (const name of ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorPolicy']) {
      this.refs[name]?.classList.add('hidden');
    }
  }

  #showPolicyError(message) {
    this.#openDiscount();
    this.#clearErrors();
    this.refs.cartDiscountErrorPolicy.textContent = message;
    this.refs.cartDiscountErrorPolicy.classList.remove('hidden');
    this.refs.cartDiscountError.classList.remove('hidden');
  }

  #openDiscount() {
    const accordion = this.closest('accordion-custom');
    // Keep the notice visible even if the accordion module upgrades after us.
    accordion?.setAttribute('open-by-default-on-mobile', '');
    accordion?.setAttribute('open-by-default-on-desktop', '');
    const details = this.closest('details');
    if (details) details.open = true;
  }

  showCouponReturnError(message) {
    this.#showPolicyError(message);
  }

  applyWalletCoupon = async (code) => {
    this.#openDiscount();
    const input = this.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement) || !this.dataset.sectionId || !isWalletCoupon(code)) {
      this.#showPolicyError('Your coupon link could not be verified. Please return to your coupon wallet and try again.');
      return false;
    }
    input.value = code;
    return this.#applyCode(code, input);
  };

  // The checkout controller owns when native checkout starts. This component
  // remains the sole owner of the native cart-discount write and readback.
  applyCheckoutCoupon = async (code, onConfirmed) => {
    // The inline checkout chooser owns this interaction. Do not open a second
    // discount form above it while the selected coupon is being applied.
    const input = this.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement) || !this.dataset.sectionId) {
      this.#showPolicyError("We couldn't prepare this coupon. Please use the discount field or continue without it.");
      return false;
    }
    input.value = code;
    return this.#applyCode(code, input, undefined, onConfirmed);
  };

  // Explicit "No coupon" is a real native removal, never just a visual state.
  clearCheckoutDiscount = async (onConfirmed) => {
    if (!this.dataset.sectionId) return false;
    const controller = this.#createAbortController();
    if (!controller) return false;
    let deferred;
    try {
      deferred = CartDiscountUpdateEvent.createPromise();
      this.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: [], promise: deferred.promise }));
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ discount: '', sections: [this.dataset.sectionId] }) }),
        signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]),
      });
      if (!response.ok) throw Error('Discount removal not confirmed');
      const data = await response.json();
      const html = data.sections?.[this.dataset.sectionId];
      const section = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${this.dataset.sectionId}`) : null;
      const empty = Array.isArray(data.discount_codes) && data.discount_codes.length === 0 && section && section.querySelectorAll('.cart-discount__pill').length === 0;
      deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
      if (!empty) {
        this.#showPolicyError('Discount removal could not be confirmed. Please refresh your bag.');
        return false;
      }
      // Persist the shopper's explicit opt-out before the successful section morph.
      if (typeof onConfirmed === 'function') await onConfirmed();
      const input = this.querySelector('input[name="discount"]');
      if (input instanceof HTMLInputElement) input.value = '';
      morphSection(this.dataset.sectionId, html, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
      return true;
    } catch {
      deferred?.reject(new Error('Discount removal could not be confirmed.'));
      if (!controller.signal.aborted) this.#showPolicyError('Discount removal could not be confirmed. Please refresh your bag.');
      return false;
    } finally {
      this.#finishOperation(controller);
    }
  };

  chooseCoupon = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const controller = this.#createAbortController();
    if (!controller) return;
    try {
      const codes = await readCurrentDiscountCodes(this.dataset.cartUrl, controller.signal);
      if (controller.signal.aborted || !this.isConnected) return;
      if (codes.length) this.#showPolicyError(discountConflictMessage(codes, true));
      else this.dispatchEvent(new CustomEvent('calvielle:open-checkout-coupons', { bubbles: true, detail: { source: this, autoCheckout: false } }));
    } catch (error) {
      if (!controller.signal.aborted) this.#showPolicyError(error instanceof DiscountPolicyError ? error.message : "We couldn't verify your current discounts. Please try again.");
    } finally {
      this.#finishOperation(controller);
    }
  };

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;
    await this.#applyCode(discountCode.value, discountCode, event);
  };

  async #applyCode(value, discountCode, event, onConfirmed) {
    const abortController = this.#createAbortController();
    if (!abortController) return false;
    let deferredPromise;

    try {
      const discountCodeValue = singleDiscountCode(value);
      const existingDiscounts = await readCurrentDiscountCodes(this.dataset.cartUrl, abortController.signal);
      if (abortController.signal.aborted || !this.isConnected) return false;
      if (existingDiscounts.length) {
        this.#showPolicyError(discountConflictMessage(existingDiscounts, isWelcomeCoupon(discountCodeValue)));
        return false;
      }
      deferredPromise = CartDiscountUpdateEvent.createPromise();
      this.dispatchEvent(new CartDiscountUpdateEvent({discountCodes: [{code: discountCodeValue}], promise: deferredPromise.promise}));
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: discountCodeValue,
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: AbortSignal.any([abortController.signal, AbortSignal.timeout(15000)]),
      });
      if (!response.ok) throw new Error('Discount update was not confirmed.');
      const data = await response.json();

      const normalizedCode = discountCodeValue.toLocaleUpperCase('en-US');
      const confirmedDiscounts = Array.isArray(data.discount_codes)
        ? data.discount_codes.filter((/** @type {{ code?: unknown; applicable?: unknown; }} */ discount) => {
          return typeof discount?.code === 'string'
            && discount.code.toLocaleUpperCase('en-US') === normalizedCode
            && discount.applicable === true;
        })
        : [];
      // `/cart/update.js` is authoritative. A missing or merely inapplicable
      // echo is an unknown write, not permission to continue to checkout.
      if (confirmedDiscounts.length !== 1) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return false;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return false;
      }
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      const renderedCodes = Array.from(discountCodes)
        .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
        .filter((code) => typeof code === 'string')
        .map(code => code.toLocaleUpperCase('en-US'));
      // The rendered cart is a second, independent native readback. The
      // one-code order policy means exactly this one applicable code must be
      // present before checkout receives a true confirmation.
      if (!section || renderedCodes.length !== 1 || renderedCodes[0] !== normalizedCode) {
        this.#handleDiscountError('discount_code');
        discountCode.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return false;
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // A later explicit code entry supersedes an earlier "No coupon" choice,
      // but only after both native readbacks prove that code was applied.
      if (event) this.dispatchEvent(new CustomEvent('calvielle:discount-applied', { bubbles: true, detail: { source: this } }));
      // Checkout must follow the platform's applicable-code readback, but it
      // must happen before a section morph disconnects the original submitter.
      if (typeof onConfirmed === 'function') {
        // A stale parent attempt may decline the handoff (drawer closed or the
        // user backed out). Preserve the confirmed cart update by morphing,
        // but never submit checkout from that obsolete attempt.
        if (await onConfirmed() === true) return true;
      }
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
      return true;
    } catch (error) {
      const message = error instanceof DiscountPolicyError ? error.message : "We couldn't confirm the discount update. Please refresh your bag and try again.";
      deferredPromise?.reject(new Error(message));
      if (!abortController.signal.aborted) {
        this.#showPolicyError(message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
      return false;
    } finally {
      this.#finishOperation(abortController);
      if (event) cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const abortController = this.#createAbortController();
    if (!abortController) return;
    let deferredPromise;

    try {
      const existingDiscounts = await readCurrentDiscountCodes(this.dataset.cartUrl, abortController.signal);
      if (abortController.signal.aborted || !this.isConnected) return;
      const index = existingDiscounts.findIndex(code => code.toUpperCase() === discountCode.toUpperCase());
      if (index === -1) {
        this.#showPolicyError('This discount is no longer applied. Refresh your bag to update it.');
        return;
      }
      existingDiscounts.splice(index, 1);
      deferredPromise = CartDiscountUpdateEvent.createPromise();
      this.dispatchEvent(new CartDiscountUpdateEvent({discountCodes: existingDiscounts.map(code => ({code})), promise: deferredPromise.promise}));
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: AbortSignal.any([abortController.signal, AbortSignal.timeout(15000)]),
      });
      if (!response.ok) throw new Error('Discount removal was not confirmed.');
      const data = await response.json();

      if (existingDiscounts.length === 0) {
        const html = data.sections?.[this.dataset.sectionId];
        const section = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${this.dataset.sectionId}`) : null;
        if (!Array.isArray(data.discount_codes) || data.discount_codes.length !== 0 || !section || section.querySelectorAll('.cart-discount__pill').length !== 0) {
          throw new Error('Discount removal was not confirmed.');
        }
        this.dispatchEvent(new CustomEvent('calvielle:discount-removed', { bubbles: true, detail: { source: this } }));
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      const message = error instanceof DiscountPolicyError ? error.message : "We couldn't confirm the discount update. Please refresh your bag and try again.";
      deferredPromise?.reject(new Error(message));
      if (!abortController.signal.aborted) {
        this.#showPolicyError(message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#finishOperation(abortController);
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

async function resumeWalletCoupon() {
  // Only the visible cart page owns a return. A header drawer may be first in
  // DOM order but must never consume the selection or show a hidden error.
  const component = Array.from(document.querySelectorAll('cart-discount-component'))
    .find(element => element instanceof CartDiscount && !element.closest('theme-drawer'));
  const showError = (message) => {
    if (component) component.showCouponReturnError(message);
    else {
      const container = document.querySelector('.cart-page__items') || document.querySelector('main');
      const notice = document.createElement('p');
      notice.className = 'cart-discount__policy';
      notice.setAttribute('role', 'alert');
      notice.textContent = message;
      container?.append(notice);
    }
  };
  try {
    const code = consumeWalletCoupon(Theme.routes.cart_url);
    if (!code) return;
    if (component) await component.applyWalletCoupon(code);
    else showError('Add a piece to your bag, then return to your coupon wallet to apply your gift.');
  } catch (error) {
    showError(error instanceof DiscountPolicyError ? error.message : "We couldn't confirm your coupon selection. Please return to your coupon wallet and try again.");
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resumeWalletCoupon, { once: true });
else void resumeWalletCoupon();
